// PeriodType Enum for date filtering
export const PeriodType = {
  NONE: 0,
  MONTH: 1,
  PERIOD: 2, // 2 months (not used in UI)
  QUARTER: 3,
  TRIMESTER: 4, // 4 months (not used in UI)
  HALF: 6,
  YEAR: 12
};
/**
 * Application Configuration and Constants
 */

export const CONFIG = {
  // API Configuration
  API: {
    DEFAULT_BASE_URL: 'https://localhost:7189',
    ENDPOINTS: {
      // ── Not used by current add-in (taskpane + ribbon). Raw data + client-side reports only. ──
      // PARTIES: '/api/v1/Party/GetAllParties',
      // GROUP_ENTITIES: '/api/v1/GroupEntity/GetAllGroupEntities',
      // TRIAL_BALANCE: '/api/v1/TrialBalance/GetTrialBalance',
      // PNL_REPORT: '/api/v1/FinancialReports/GetProfitAndLoss',
      // BALANCE_SHEET: '/api/v1/FinancialReports/GetBalanceSheet',
      // TRIAL_BALANCE_V2: '/api/v2/TrialBalance/GetTrialBalance',
      // PNL_REPORT_V2: '/api/v2/FinancialReports/GetProfitAndLoss',
      // BALANCE_SHEET_V2: '/api/v2/FinancialReports/GetBalanceSheet',
      // CASH_FLOW_V2: '/api/v2/FinancialReports/GetCashFlow',
      // ── Used by Setup / Re-download raw data ──
      TRIAL_BALANCE_FLAT: '/api/v2/TrialBalance/GetTrialBalanceFlat',
      // Raw sync + reference
      GET_CURRENCIES: '/api/v2/ReferenceData/GetCurrencies',
      GET_EXCHANGE_RATES: '/api/v2/ReferenceData/GetExchangeRates',
      GET_INTERCOMPANY_ELIMINATIONS: '/api/v2/ExcelRawData/GetIntercompanyEliminations',
      GET_PARTY_OWNERSHIPS: '/api/v2/ExcelRawData/GetPartyOwnerships',
      GET_PARTIES_DETAIL: '/api/v2/ExcelRawData/GetPartiesDetail',
      GET_GROUP_ENTITIES: '/api/v1/GroupEntity/GetAllGroupEntities',
      GET_MASTER_PROFILES: '/api/v2/MasterChart/GetProfiles',
      GET_MASTER_ACCOUNTS: '/api/v2/MasterChart/GetMasterAccounts',
      GET_ACCOUNT_MAPPINGS: '/api/v2/MasterChart/GetAccountMappings',
      /** Excel add-in AI: workbook context → dashboard plan (uses AIChat on server) */
      EXCEL_ASSISTANT_PLAN: '/api/v2/ExcelAssistant/PlanDashboard'
    },
    DEFAULT_PROFILE_ID: 1
  },

  // Date Configuration
  DATE: {
    START_YEAR: 2020,
    MONTH_NAMES: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ]
  },

  // Office.js Configuration
  OFFICE: {
    IDENTITY_API_VERSION: '1.3',
    HOST_TYPE: 'Excel'
  },

  // Sheet Names
  SHEETS: {
    TRIAL_BALANCE_PREFIX: 'TrialBalance_',
    PNL_PREFIX: 'PnL_',
    BALANCE_SHEET_PREFIX: 'BalanceSheet_',
    ALL_COMPANIES_SUFFIX: 'AllCompanies',
    /** Interactive config / filter panel sheet */
    CONFIG_SHEET: '_Config',
    /** Hidden sheet: PartyId + Name rows for company dropdown validation (not shown on _Config) */
    PARTY_PICK_SHEET: '_PartyPick',
    /** Hidden: group entity id + label rows (not companies) */
    GROUP_PICK_SHEET: '_GroupPick',
    /** Hidden: period labels for _Config — rebuilt when Frequency changes */
    PERIOD_PICK_SHEET: '_PeriodPick',
    /** Hidden: master profile labels */
    PROFILE_PICK_SHEET: '_ProfilePick',
    /** Raw data sheets — DO NOT rename; sync flow targets these by name */
    RAW_TRIAL_BALANCE: 'Raw_TrialBalance',
    RAW_PARTIES: 'Raw_Parties',
    RAW_CURRENCIES: 'Raw_Currencies',
    RAW_EXCHANGE_RATES: 'Raw_ExchangeRates',
    RAW_ELIMINATIONS: 'Raw_Eliminations',
    RAW_OWNERSHIP: 'Raw_Ownership',
    RAW_MASTER_PROFILES: 'Raw_MasterProfiles',
    RAW_MASTER_ACCOUNTS: 'Raw_MasterAccounts',
    RAW_ACCOUNT_MAPPINGS: 'Raw_AccountMappings',
    /** Report output sheets — regenerated on each Sync from raw data */
    RPT_TRIAL_BALANCE: 'Rpt_TrialBalance',
    RPT_PROFIT_LOSS: 'Rpt_ProfitLoss',
    RPT_BALANCE_SHEET: 'Rpt_BalanceSheet',
    RPT_CASH_FLOW: 'Rpt_CashFlow',
    /** Flat TB data for PivotTables / slicers */
    PIVOT_TB_SHEET: 'PivotData_TB',
  },

  // Named ranges on _Config sheet — must match buildControlSheet cell addresses exactly
  NAMED_RANGES: {
    SELECTED_COMPANIES: 'cfg_SelectedCompanies', // B5: comma-separated party IDs
    GROUP_ENTITY:       'cfg_GroupEntity',        // B7: single group entity ID
    YEAR:               'cfg_Year',               // D5
    FREQUENCY:          'cfg_Frequency',          // F5: Monthly/Quarterly/Half-year/Full year
    PERIOD:             'cfg_Period',             // H5: 1-12 or Q1-Q4 etc.
    REPORT_CURRENCY:    'cfg_ReportCurrency',     // K5: ISO code or blank
    NUMBERS_IN:         'cfg_NumbersIn',          // M5: Whole/Thousands/Millions
    PROFILE_ID:         'cfg_ProfileId',          // master profile — label "(id)" parsed in add-in
    // SHOW_ADJUSTED and INCLUDE_HISTORICAL removed — not needed
    SYNC_SCOPE:         'cfg_SyncScope',          // C10: All/TrialBalance/Parties/etc.
    LAST_SYNC_TIME:     'cfg_LastSync',           // F10
    /** Trial balance only vs P&L / BS / CF — C11 */
    REPORT_PACK:        'cfg_ReportPack',
    /** In-sheet status while syncing (task pane + Sync Data) — F11 */
    SYNC_STATUS:        'cfg_SyncStatus'
  },

  // UI Configuration
  UI: {
    STATUS_DISPLAY_DURATION: 3000, // milliseconds
    STATUS_COLORS: {
      success: '#107c10',
      error: '#d13438',
      loading: '#106ebe',
      info: '#323130'
    }
  },

  // Excel Formatting
  EXCEL: {
    HEADER_COLOR: '#4472C4',
    HEADER_FONT_COLOR: 'white',
    SECTION_COLOR: '#0070C0',
    NUMBER_FORMAT: '#,##0',  // Format: 1,234 (whole numbers, matching Blazor N0)
    NUMBER_FORMAT_THOUSANDS: '#,##0.00',  // Format: 1,234.56 (in thousands, matching Blazor N2)
    NUMBER_FORMAT_MILLIONS: '#,##0.0',  // Format: 1,234.5 (in millions, matching Blazor N1)
    COLUMN_WIDTHS: {
      DEFAULT_LABEL: 300,
      DEFAULT_VALUE: 120
    },
    /** Excel table name for pivot-ready TB export (Insert → PivotTable from this table) */
    PIVOT_TB_TABLE_NAME: 'Tbl_PivotTB'
  },

  // NumbersIn Enum
  NUMBERS_IN: {
    WHOLE: 'whole',
    THOUSANDS: 'thousands',
    MILLIONS: 'millions'
  },

  // Error Codes
  ERROR_CODES: {
    USER_NOT_SIGNED_IN: 13001,
    CONSENT_REQUIRED: 13002,
    RUNTIME_ERROR: 13003
  }
};

/**
 * Get API base URL dynamically based on source URL
 */
export function getApiBaseUrl() {
  const sourceUrl = window.location.hostname;
  
  // Generic mapping: <prefix>-addin.corvenia.no -> <prefix>apis.corvenia.com
  const addinNoMatch = sourceUrl.match(/^([a-z0-9-]+)-addin\.corvenia\.no$/);
  if (addinNoMatch) {
    const prefix = addinNoMatch[1];
    return `https://${prefix}apis.corvenia.com`;
  }
  
  // Fallback to localhost for unknown domains
  return CONFIG.API.DEFAULT_BASE_URL;
}